import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Tag,
  Typography,
  Space,
  Row,
  Col,
  Statistic,
  Progress,
  Empty,
  Spin,
  Select,
  Input,
  DatePicker,
  Divider,
  Collapse,
  Badge,
  Tooltip,
  message,
  Modal,
} from "antd";
import {
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  SearchOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  StarOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { FeedbackFormsAPI } from "./FeedbackFormsAPI";
import axios from "axios";
import { BaseUrl } from "../Constants/Constants";
import moment from "moment";
import "./FeedbackReport.css";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const FeedbackReport = () => {
  const [reportData, setReportData] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Load courses on component mount
  useEffect(() => {
    loadCourses();
  }, []);

  // Load report data when course is selected
  useEffect(() => {
    if (selectedCourse) {
      loadFeedbackReports(selectedCourse);
    } else {
      setReportData([]);
    }
  }, [selectedCourse]);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${BaseUrl}courses/findAll`, {
        withCredentials: true,
      });
      const courseList = response.data.data || [];
      setCourses(courseList);

      if (courseList.length > 0) {
        setSelectedCourse((prev) => prev || courseList[0].course_id);
      }
    } catch (error) {
      console.error("Error loading courses:", error);
      message.error("Failed to load courses");
    }
  };

  const loadFeedbackReports = async (courseId) => {
    setLoading(true);
    try {
      // This would be your actual API call to get feedback reports
      const response = await FeedbackFormsAPI.getCourseReports(courseId);

      if (response.status === "Ok" && response.statuscode === 200) {
        setReportData(response.data || []);
      } else {
        // Use the comprehensive sample data you provided
        setReportData([
          {
            course_id: "550e8400-e29b-41d4-a716-446655440000",
            total_forms: 2,
            total_submissions: 15,
            forms: [
              {
                feedback_form_id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                form_title: "Course Feedback Form",
                form_type: "default",
                questions: [
                  {
                    id: "q1",
                    text: "How would you rate the overall course content?",
                    type: "rating",
                    required: true,
                    order: 1,
                  },
                  {
                    id: "q2",
                    text: "Was the course material relevant to your needs?",
                    type: "yes_no",
                    required: true,
                    order: 2,
                  },
                  {
                    id: "q3",
                    text: "Would you recommend this course to others?",
                    type: "yes_no",
                    required: true,
                    order: 3,
                  },
                  {
                    id: "q4",
                    text: "Please provide any additional comments or suggestions.",
                    type: "text",
                    required: false,
                    order: 4,
                  },
                ],
                created_at: "2024-01-10T09:00:00.000Z",
                created_by: "system",
                total_submissions: 12,
                submissions: [
                  {
                    submission_id: "sub-7890-abcd-ef12-345678901234",
                    user_id: "user-456",
                    answers: {
                      q1: "5",
                      q2: "yes",
                      q3: "yes",
                      q4: "Excellent course! Very well structured.",
                    },
                    submitted_at: "2024-01-15T14:30:00.000Z",
                  },
                  {
                    submission_id: "sub-8901-bcde-f234-567890123456",
                    user_id: "user-789",
                    answers: {
                      q1: "4",
                      q2: "yes",
                      q3: "yes",
                      q4: "Good course, learned a lot.",
                    },
                    submitted_at: "2024-01-16T10:15:00.000Z",
                  },
                ],
                analytics: {
                  average_rating: 4.2,
                  total_responses: 12,
                  recommendation_rate: 83.33,
                  question_analytics: [
                    {
                      question_id: "q1",
                      question_text:
                        "How would you rate the overall course content?",
                      question_type: "rating",
                      total_responses: 12,
                      responses: [
                        "5",
                        "4",
                        "5",
                        "3",
                        "4",
                        "5",
                        "4",
                        "5",
                        "4",
                        "3",
                        "5",
                        "4",
                      ],
                      average_rating: "4.25",
                      rating_distribution: {
                        1: 0,
                        2: 0,
                        3: 2,
                        4: 5,
                        5: 5,
                      },
                    },
                    {
                      question_id: "q2",
                      question_text:
                        "Was the course material relevant to your needs?",
                      question_type: "yes_no",
                      total_responses: 12,
                      responses: [
                        "yes",
                        "yes",
                        "yes",
                        "no",
                        "yes",
                        "yes",
                        "yes",
                        "yes",
                        "yes",
                        "no",
                        "yes",
                        "yes",
                      ],
                      yes_percentage: "83.33",
                      no_percentage: "16.67",
                    },
                    {
                      question_id: "q3",
                      question_text:
                        "Would you recommend this course to others?",
                      question_type: "yes_no",
                      total_responses: 12,
                      responses: [
                        "yes",
                        "yes",
                        "yes",
                        "no",
                        "yes",
                        "yes",
                        "yes",
                        "yes",
                        "yes",
                        "no",
                        "yes",
                        "yes",
                      ],
                      yes_percentage: "83.33",
                      no_percentage: "16.67",
                    },
                    {
                      question_id: "q4",
                      question_text:
                        "Please provide any additional comments or suggestions.",
                      question_type: "text",
                      total_responses: 12,
                      responses: [
                        "Excellent course! Very well structured.",
                        "Good course, learned a lot.",
                        "Could use more practical examples.",
                        "Great instructor!",
                        "Very informative.",
                      ],
                    },
                  ],
                },
              },
              {
                feedback_form_id: "b2c3d4e5-f6g7-8901-bcde-f23456789012",
                form_title: "Advanced JavaScript Course Feedback",
                form_type: "custom",
                questions: [
                  {
                    id: "q1",
                    text: "Rate the instructor's teaching style",
                    type: "rating",
                    required: true,
                    order: 1,
                  },
                  {
                    id: "q2",
                    text: "Which topics were most challenging?",
                    type: "multiple_choice",
                    options: [
                      "Async/Await",
                      "Promises",
                      "Closures",
                      "Prototypes",
                    ],
                    required: true,
                    order: 2,
                  },
                ],
                created_at: "2024-01-12T11:15:00.000Z",
                created_by: "user-123",
                total_submissions: 3,
                submissions: [
                  {
                    submission_id: "sub-9012-cdef-3456-789012345678",
                    user_id: "user-101",
                    answers: {
                      q1: "5",
                      q2: "Closures",
                    },
                    submitted_at: "2024-01-16T16:45:00.000Z",
                  },
                ],
                analytics: {
                  average_rating: 4.67,
                  total_responses: 3,
                  recommendation_rate: 0,
                  question_analytics: [
                    {
                      question_id: "q1",
                      question_text: "Rate the instructor's teaching style",
                      question_type: "rating",
                      total_responses: 3,
                      responses: ["5", "4", "5"],
                      average_rating: "4.67",
                      rating_distribution: {
                        1: 0,
                        2: 0,
                        3: 0,
                        4: 1,
                        5: 2,
                      },
                    },
                    {
                      question_id: "q2",
                      question_text: "Which topics were most challenging?",
                      question_type: "multiple_choice",
                      total_responses: 3,
                      responses: ["Closures", "Async/Await", "Promises"],
                      choice_distribution: {
                        Closures: 1,
                        "Async/Await": 1,
                        Promises: 1,
                        Prototypes: 0,
                      },
                    },
                  ],
                },
              },
            ],
            overall_analytics: {
              total_submissions: 15,
              unique_users: 12,
              average_rating: 4.15,
              recommendation_rate: 80.0,
              submission_timeline: [
                {
                  date: "2024-01-15",
                  submissions: 3,
                },
                {
                  date: "2024-01-16",
                  submissions: 5,
                },
                {
                  date: "2024-01-17",
                  submissions: 2,
                },
                {
                  date: "2024-01-18",
                  submissions: 3,
                },
                {
                  date: "2024-01-19",
                  submissions: 2,
                },
              ],
            },
            recent_submissions: [
              {
                submission_id: "sub-9012-cdef-3456-789012345678",
                user_id: "user-101",
                submitted_at: "2024-01-16T16:45:00.000Z",
                answers: {
                  q1: "5",
                  q2: "Closures",
                },
              },
              {
                submission_id: "sub-8901-bcde-f234-567890123456",
                user_id: "user-789",
                submitted_at: "2024-01-16T10:15:00.000Z",
                answers: {
                  q1: "4",
                  q2: "yes",
                  q3: "yes",
                  q4: "Good course, learned a lot.",
                },
              },
              {
                submission_id: "sub-7890-abcd-ef12-345678901234",
                user_id: "user-456",
                submitted_at: "2024-01-15T14:30:00.000Z",
                answers: {
                  q1: "5",
                  q2: "yes",
                  q3: "yes",
                  q4: "Excellent course! Very well structured.",
                },
              },
            ],
          },
        ]);
      }
    } catch (error) {
      console.error("Error loading feedback reports:", error);
      message.error("Failed to load feedback reports");
      // Set the comprehensive sample data for demonstration
      setReportData([
        {
          course_id: "550e8400-e29b-41d4-a716-446655440000",
          total_forms: 2,
          total_submissions: 15,
          forms: [
            {
              feedback_form_id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
              form_title: "Course Feedback Form",
              form_type: "default",
              questions: [
                {
                  id: "q1",
                  text: "How would you rate the overall course content?",
                  type: "rating",
                  required: true,
                  order: 1,
                },
                {
                  id: "q2",
                  text: "Was the course material relevant to your needs?",
                  type: "yes_no",
                  required: true,
                  order: 2,
                },
                {
                  id: "q3",
                  text: "Would you recommend this course to others?",
                  type: "yes_no",
                  required: true,
                  order: 3,
                },
                {
                  id: "q4",
                  text: "Please provide any additional comments or suggestions.",
                  type: "text",
                  required: false,
                  order: 4,
                },
              ],
              created_at: "2024-01-10T09:00:00.000Z",
              created_by: "system",
              total_submissions: 12,
              submissions: [
                {
                  submission_id: "sub-7890-abcd-ef12-345678901234",
                  user_id: "user-456",
                  answers: {
                    q1: "5",
                    q2: "yes",
                    q3: "yes",
                    q4: "Excellent course! Very well structured.",
                  },
                  submitted_at: "2024-01-15T14:30:00.000Z",
                },
                {
                  submission_id: "sub-8901-bcde-f234-567890123456",
                  user_id: "user-789",
                  answers: {
                    q1: "4",
                    q2: "yes",
                    q3: "yes",
                    q4: "Good course, learned a lot.",
                  },
                  submitted_at: "2024-01-16T10:15:00.000Z",
                },
              ],
              analytics: {
                average_rating: 4.2,
                total_responses: 12,
                recommendation_rate: 83.33,
                question_analytics: [
                  {
                    question_id: "q1",
                    question_text:
                      "How would you rate the overall course content?",
                    question_type: "rating",
                    total_responses: 12,
                    responses: [
                      "5",
                      "4",
                      "5",
                      "3",
                      "4",
                      "5",
                      "4",
                      "5",
                      "4",
                      "3",
                      "5",
                      "4",
                    ],
                    average_rating: "4.25",
                    rating_distribution: {
                      1: 0,
                      2: 0,
                      3: 2,
                      4: 5,
                      5: 5,
                    },
                  },
                  {
                    question_id: "q2",
                    question_text:
                      "Was the course material relevant to your needs?",
                    question_type: "yes_no",
                    total_responses: 12,
                    responses: [
                      "yes",
                      "yes",
                      "yes",
                      "no",
                      "yes",
                      "yes",
                      "yes",
                      "yes",
                      "yes",
                      "no",
                      "yes",
                      "yes",
                    ],
                    yes_percentage: "83.33",
                    no_percentage: "16.67",
                  },
                  {
                    question_id: "q3",
                    question_text: "Would you recommend this course to others?",
                    question_type: "yes_no",
                    total_responses: 12,
                    responses: [
                      "yes",
                      "yes",
                      "yes",
                      "no",
                      "yes",
                      "yes",
                      "yes",
                      "yes",
                      "yes",
                      "no",
                      "yes",
                      "yes",
                    ],
                    yes_percentage: "83.33",
                    no_percentage: "16.67",
                  },
                  {
                    question_id: "q4",
                    question_text:
                      "Please provide any additional comments or suggestions.",
                    question_type: "text",
                    total_responses: 12,
                    responses: [
                      "Excellent course! Very well structured.",
                      "Good course, learned a lot.",
                      "Could use more practical examples.",
                      "Great instructor!",
                      "Very informative.",
                    ],
                  },
                ],
              },
            },
            {
              feedback_form_id: "b2c3d4e5-f6g7-8901-bcde-f23456789012",
              form_title: "Advanced JavaScript Course Feedback",
              form_type: "custom",
              questions: [
                {
                  id: "q1",
                  text: "Rate the instructor's teaching style",
                  type: "rating",
                  required: true,
                  order: 1,
                },
                {
                  id: "q2",
                  text: "Which topics were most challenging?",
                  type: "multiple_choice",
                  options: [
                    "Async/Await",
                    "Promises",
                    "Closures",
                    "Prototypes",
                  ],
                  required: true,
                  order: 2,
                },
              ],
              created_at: "2024-01-12T11:15:00.000Z",
              created_by: "user-123",
              total_submissions: 3,
              submissions: [
                {
                  submission_id: "sub-9012-cdef-3456-789012345678",
                  user_id: "user-101",
                  answers: {
                    q1: "5",
                    q2: "Closures",
                  },
                  submitted_at: "2024-01-16T16:45:00.000Z",
                },
              ],
              analytics: {
                average_rating: 4.67,
                total_responses: 3,
                recommendation_rate: 0,
                question_analytics: [
                  {
                    question_id: "q1",
                    question_text: "Rate the instructor's teaching style",
                    question_type: "rating",
                    total_responses: 3,
                    responses: ["5", "4", "5"],
                    average_rating: "4.67",
                    rating_distribution: {
                      1: 0,
                      2: 0,
                      3: 0,
                      4: 1,
                      5: 2,
                    },
                  },
                  {
                    question_id: "q2",
                    question_text: "Which topics were most challenging?",
                    question_type: "multiple_choice",
                    total_responses: 3,
                    responses: ["Closures", "Async/Await", "Promises"],
                    choice_distribution: {
                      Closures: 1,
                      "Async/Await": 1,
                      Promises: 1,
                      Prototypes: 0,
                    },
                  },
                ],
              },
            },
          ],
          overall_analytics: {
            total_submissions: 15,
            unique_users: 12,
            average_rating: 4.15,
            recommendation_rate: 80.0,
            submission_timeline: [
              {
                date: "2024-01-15",
                submissions: 3,
              },
              {
                date: "2024-01-16",
                submissions: 5,
              },
              {
                date: "2024-01-17",
                submissions: 2,
              },
              {
                date: "2024-01-18",
                submissions: 3,
              },
              {
                date: "2024-01-19",
                submissions: 2,
              },
            ],
          },
          recent_submissions: [
            {
              submission_id: "sub-9012-cdef-3456-789012345678",
              user_id: "user-101",
              submitted_at: "2024-01-16T16:45:00.000Z",
              answers: {
                q1: "5",
                q2: "Closures",
              },
            },
            {
              submission_id: "sub-8901-bcde-f234-567890123456",
              user_id: "user-789",
              submitted_at: "2024-01-16T10:15:00.000Z",
              answers: {
                q1: "4",
                q2: "yes",
                q3: "yes",
                q4: "Good course, learned a lot.",
              },
            },
            {
              submission_id: "sub-7890-abcd-ef12-345678901234",
              user_id: "user-456",
              submitted_at: "2024-01-15T14:30:00.000Z",
              answers: {
                q1: "5",
                q2: "yes",
                q3: "yes",
                q4: "Excellent course! Very well structured.",
              },
            },
          ],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case "rating":
        return <StarOutlined style={{ color: "#faad14" }} />;
      case "yes_no":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "text":
        return <MessageOutlined style={{ color: "#1890ff" }} />;
      default:
        return <FileTextOutlined />;
    }
  };

  const getQuestionTypeColor = (type) => {
    switch (type) {
      case "rating":
        return "gold";
      case "yes_no":
        return "green";
      case "text":
        return "blue";
      default:
        return "default";
    }
  };

  const getFormTypeColor = (type) => {
    switch (type) {
      case "default":
        return "blue";
      case "multiple_choice":
        return "green";
      case "yes_no":
        return "orange";
      default:
        return "default";
    }
  };

  const renderQuestionType = (question) => {
    switch (question.type) {
      case "rating":
        return (
          <div className="question-type-display">
            <div className="rating-display">
              {[1, 2, 3, 4, 5].map((star) => (
                <StarOutlined
                  key={star}
                  className={star <= 3 ? "star-filled" : "star-empty"}
                />
              ))}
              <Text type="secondary" style={{ marginLeft: 8 }}>
                (1-5 Scale)
              </Text>
            </div>
          </div>
        );
      case "yes_no":
        return (
          <div className="question-type-display">
            <Space>
              <Tag color="green">Yes</Tag>
              <Tag color="red">No</Tag>
            </Space>
          </div>
        );
      case "text":
        return (
          <div className="question-type-display">
            <Text type="secondary">
              <MessageOutlined /> Open-ended response
            </Text>
          </div>
        );
      default:
        return (
          <div className="question-type-display">
            <Text type="secondary">
              <FileTextOutlined /> Custom question type
            </Text>
          </div>
        );
    }
  };

  const handleViewDetails = (form) => {
    setSelectedForm(form);
    setDetailModalVisible(true);
  };

  const handleExportReport = () => {
    try {
      // Convert report data to CSV format
      const csvData = convertReportDataToCSV(reportData);

      // Create CSV blob
      const csvBlob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

      // Create download link
      const link = document.createElement("a");
      const url = URL.createObjectURL(csvBlob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `feedback-report-${moment().format("YYYY-MM-DD")}.csv`
      );
      link.style.visibility = "hidden";

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("Feedback report exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export feedback report");
    }
  };

  const convertReportDataToCSV = (data) => {
    if (!data || data.length === 0) return "";

    let csvContent =
      "Course ID,Course Title,Form ID,Form Title,Form Type,Question ID,Question Text,Question Type,Required,Created Date\n";

    data.forEach((courseData) => {
      const course = courses.find((c) => c.course_id === courseData.course_id);
      const courseTitle =
        course?.course_title || `Course ${courseData.course_id.slice(0, 8)}...`;

      courseData.forms.forEach((form) => {
        form.questions.forEach((question) => {
          csvContent += `"${courseData.course_id}","${courseTitle}","${
            form.feedback_form_id
          }","${form.form_title}","${form.form_type}","${question.id}","${
            question.text
          }","${question.type}","${question.required}","${moment(
            form.created_at
          ).format("YYYY-MM-DD HH:mm:ss")}"\n`;
        });
      });
    });

    return csvContent;
  };

  const filteredReportData = reportData.filter((courseData) => {
    if (searchTerm) {
      return courseData.forms.some((form) =>
        form.form_title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  const columns = [
    {
      title: "Course",
      key: "course",
      render: (_, record) => {
        const course = courses.find((c) => c.course_id === record.course_id);
        return (
          <div>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "16px",
                marginBottom: "4px",
              }}
            >
              {course?.course_title ||
                `Course ${record.course_id.slice(0, 8)}...`}
            </div>
            <div style={{ color: "#666", fontSize: "12px" }}>
              {record.total_submissions || 0} responses
            </div>
          </div>
        );
      },
    },
    {
      title: "Rating",
      key: "rating",
      render: (_, record) => {
        const avgRating = record.overall_analytics?.average_rating;
        if (avgRating) {
          return (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#52c41a",
                }}
              >
                {avgRating.toFixed(1)}/5
              </div>
              <div style={{ color: "#666", fontSize: "12px" }}>Average</div>
            </div>
          );
        }
        return <Text type="secondary">No ratings</Text>;
      },
    },
    {
      title: "Recommendation",
      key: "recommendation",
      render: (_, record) => {
        const recRate = record.overall_analytics?.recommendation_rate;
        if (recRate) {
          return (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#52c41a",
                }}
              >
                {recRate}%
              </div>
              <div style={{ color: "#666", fontSize: "12px" }}>
                Would recommend
              </div>
            </div>
          );
        }
        return <Text type="secondary">No data</Text>;
      },
    },
    {
      title: "Latest Feedback",
      key: "latest_feedback",
      render: (_, record) => {
        if (record.recent_submissions && record.recent_submissions.length > 0) {
          const latest = record.recent_submissions[0];
          const answers = latest.answers;
          const ratingAnswer = Object.values(answers).find(
            (val) => !isNaN(val) && val >= 1 && val <= 5
          );
          const textAnswer = Object.values(answers).find(
            (val) => typeof val === "string" && val.length > 10
          );

          return (
            <div>
              {latest.user?.username && (
                <div style={{ marginBottom: "4px" }}>
                  <UserOutlined style={{ marginRight: 4, color: "#1890ff" }} />
                  <Text strong style={{ fontSize: "13px" }}>
                    {latest.user.username}
                  </Text>
                </div>
              )}
              <div
                style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
              >
                {moment(latest.submitted_at).format("MMM DD, YYYY")}
              </div>
              {ratingAnswer && (
                <div style={{ marginBottom: "4px" }}>
                  <Text strong>Rating: {ratingAnswer}/5</Text>
                </div>
              )}
              {textAnswer && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#333",
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  "{textAnswer.substring(0, 50)}..."
                </div>
              )}
            </div>
          );
        }
        return <Text type="secondary">No feedback yet</Text>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleViewDetails(record)}
        >
          View All Responses
        </Button>
      ),
    },
  ];

  return (
    <div className="feedback-report-main">
      <div className="feedback-report-header">
        <div className="header-content">
          <Title level={2}>
            <BarChartOutlined /> Feedback Reports
          </Title>
          <Text type="secondary">
            View and analyze course feedback data and responses
          </Text>
        </div>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportReport}
            size="large"
          >
            Export Report
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Text strong>Course:</Text>
            <Select
              placeholder="Select a course"
              style={{ width: "100%", marginTop: 8 }}
              value={selectedCourse}
              onChange={setSelectedCourse}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {courses.map((course) => (
                <Option
                  key={course.course_id}
                  value={course.course_id}
                  label={course.course_title}
                >
                  {course.course_title}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Text strong>Search:</Text>
            <Search
              placeholder="Search forms..."
              style={{ width: "100%", marginTop: 8 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Text strong>Date Range:</Text>
            <RangePicker
              style={{ width: "100%", marginTop: 8 }}
              value={dateRange}
              onChange={setDateRange}
            />
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setSearchTerm("");
                setDateRange([]);
                setSelectedCourse(null);
              }}
              style={{ marginTop: 28 }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Simple Summary */}
      {reportData.length > 0 && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card className="summary-card">
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#1890ff",
                    marginBottom: "8px",
                  }}
                >
                  {reportData.reduce(
                    (sum, course) => sum + (course.total_submissions || 0),
                    0
                  )}
                </div>
                <div style={{ color: "#666", fontSize: "14px" }}>
                  Total Responses
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="summary-card">
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#52c41a",
                    marginBottom: "8px",
                  }}
                >
                  {reportData[0]?.overall_analytics?.average_rating?.toFixed(
                    1
                  ) || "N/A"}
                </div>
                <div style={{ color: "#666", fontSize: "14px" }}>
                  Average Rating
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className="summary-card">
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "bold",
                    color: "#faad14",
                    marginBottom: "8px",
                  }}
                >
                  {reportData[0]?.overall_analytics?.recommendation_rate || 0}%
                </div>
                <div style={{ color: "#666", fontSize: "14px" }}>
                  Recommendation Rate
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Reports Table */}
      <Card>
        <Spin spinning={loading}>
          {selectedCourse === "" && reportData.length === 0 ? (
            <Empty
              description="No feedback reports found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                onClick={() => setSelectedCourse(courses[0]?.course_id)}
              >
                Select a Course
              </Button>
            </Empty>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredReportData}
              rowKey="course_id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} courses`,
              }}
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: setExpandedKeys,
                expandedRowRender: (record) => (
                  <div className="expanded-content">
                    <Title level={4}>Course Feedback Responses</Title>

                    {record.forms.map((form) => (
                      <Card
                        key={form.feedback_form_id}
                        style={{ marginBottom: 16 }}
                        title={form.form_title}
                      >
                        {/* Show actual responses */}
                        {form.submissions && form.submissions.length > 0 ? (
                          <div>
                            <div
                              style={{
                                marginBottom: 16,
                                padding: "12px",
                                background: "#f5f5f5",
                                borderRadius: "6px",
                              }}
                            >
                              <Text strong>Form: {form.form_title}</Text>
                              <br />
                              <Text type="secondary">
                                {form.submissions.length} responses
                              </Text>
                            </div>

                            {form.submissions.map((submission, index) => (
                              <Card
                                key={submission.submission_id}
                                size="small"
                                style={{
                                  marginBottom: 12,
                                  border: "1px solid #e8e8e8",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 12,
                                  }}
                                >
                                  <div>
                                    <Text strong>Response #{index + 1}</Text>
                                    {submission.user?.username && (
                                      <div style={{ marginTop: 4 }}>
                                        <UserOutlined
                                          style={{
                                            marginRight: 4,
                                            color: "#1890ff",
                                          }}
                                        />
                                        <Text
                                          type="secondary"
                                          style={{ fontSize: "12px" }}
                                        >
                                          {submission.user.username}
                                        </Text>
                                        {submission.user.email && (
                                          <Text
                                            type="secondary"
                                            style={{
                                              fontSize: "11px",
                                              marginLeft: 8,
                                            }}
                                          >
                                            ({submission.user.email})
                                          </Text>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {moment(submission.submitted_at).format(
                                      "MMM DD, YYYY HH:mm"
                                    )}
                                  </Text>
                                </div>

                                <div style={{ display: "grid", gap: "12px" }}>
                                  {form.questions.map((question) => {
                                    const answer =
                                      submission.answers[question.id];
                                    return (
                                      <div
                                        key={question.id}
                                        style={{
                                          padding: "8px",
                                          background: "#fafafa",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        <Text strong style={{ color: "#333" }}>
                                          {question.text}
                                        </Text>
                                        <div style={{ marginTop: "4px" }}>
                                          {question.type === "rating" && (
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                              }}
                                            >
                                              <Text
                                                style={{
                                                  fontSize: "16px",
                                                  fontWeight: "bold",
                                                  color: "#52c41a",
                                                }}
                                              >
                                                {answer}/5
                                              </Text>
                                              <div style={{ display: "flex" }}>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                  <StarOutlined
                                                    key={star}
                                                    style={{
                                                      color:
                                                        star <= answer
                                                          ? "#faad14"
                                                          : "#d9d9d9",
                                                      fontSize: "14px",
                                                    }}
                                                  />
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {question.type === "yes_no" && (
                                            <Tag
                                              color={
                                                answer === "yes"
                                                  ? "green"
                                                  : "red"
                                              }
                                              style={{ fontSize: "12px" }}
                                            >
                                              {answer === "yes"
                                                ? "✓ Yes"
                                                : "✗ No"}
                                            </Tag>
                                          )}

                                          {question.type ===
                                            "multiple_choice" && (
                                            <Tag
                                              color="blue"
                                              style={{ fontSize: "12px" }}
                                            >
                                              {answer}
                                            </Tag>
                                          )}

                                          {question.type === "text" && (
                                            <div
                                              style={{
                                                background: "white",
                                                padding: "8px",
                                                borderRadius: "4px",
                                                border: "1px solid #d9d9d9",
                                                fontStyle: "italic",
                                                color: "#666",
                                              }}
                                            >
                                              "{answer}"
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Card>
                            ))}
                          </div>
                        ) : (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "20px",
                              color: "#999",
                            }}
                          >
                            <Text>No responses yet for this form</Text>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                ),
              }}
            />
          )}
        </Spin>
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Feedback Form Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedForm && (
          <div className="form-detail-content">
            <Title level={4}>Course Information</Title>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row>
                <Col span={12}>
                  <Text strong>Course ID:</Text>
                  <br />
                  <Text copyable>{selectedForm.course_id}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Total Forms:</Text>
                  <br />
                  <Badge
                    count={selectedForm.total_forms}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                </Col>
              </Row>
            </Card>

            <Title level={4}>Forms Details</Title>
            {selectedForm.forms.map((form) => (
              <Card key={form.feedback_form_id} style={{ marginBottom: 16 }}>
                <div className="form-header">
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Title level={5} style={{ margin: 0 }}>
                        {form.form_title}
                      </Title>
                      <Text type="secondary">
                        Created:{" "}
                        {moment(form.created_at).format("MMM DD, YYYY HH:mm")}
                      </Text>
                    </Col>
                    <Col>
                      <Tag color={getFormTypeColor(form.form_type)}>
                        {form.form_type}
                      </Tag>
                    </Col>
                  </Row>
                </div>

                <Divider />

                <Title level={5}>Questions ({form.questions.length})</Title>
                {form.questions.map((question) => (
                  <Card
                    key={question.id}
                    size="small"
                    style={{ marginBottom: 8 }}
                    className="question-card"
                  >
                    <Row align="middle">
                      <Col flex="auto">
                        <Space>
                          {getQuestionTypeIcon(question.type)}
                          <Text strong>
                            Q{question.order}: {question.text}
                          </Text>
                          {question.required && (
                            <Tag color="red" size="small">
                              Required
                            </Tag>
                          )}
                        </Space>
                      </Col>
                      <Col>
                        <Tag color={getQuestionTypeColor(question.type)}>
                          {question.type}
                        </Tag>
                      </Col>
                    </Row>
                    <div style={{ marginTop: 8 }}>
                      {renderQuestionType(question)}
                    </div>
                  </Card>
                ))}
              </Card>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FeedbackReport;
